import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import { useSelector } from 'react-redux';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */
interface OnboardingContextType {
    /** Whether the welcome modal is visible */
    showWelcomeModal: boolean;
    /** Whether the completion modal is visible */
    showCompletionModal: boolean;
    /** Whether the Joyride tour is running */
    runTour: boolean;
    /** Set whether the tour is running */
    setRunTour: (run: boolean) => void;
    /** Current step index (controlled mode) */
    stepIndex: number;
    /** Set step index manually */
    setStepIndex: (index: number) => void;
    /** Dismiss welcome modal and start the Joyride tour */
    startTour: () => void;
    /** Mark onboarding as complete and stop the tour */
    completeTour: () => void;
    /** Reset persisted flag so the tour can be replayed */
    resetTour: () => void;
    /** Dismiss welcome modal without starting the tour */
    skipOnboarding: () => void;
    /** Dismiss the completion modal */
    dismissCompletion: () => void;
}

const OnboardingContext = createContext<OnboardingContextType | undefined>(undefined);

/* ------------------------------------------------------------------ */
/*  Hook                                                               */
/* ------------------------------------------------------------------ */
export const useOnboarding = (): OnboardingContextType => {
    const ctx = useContext(OnboardingContext);
    if (!ctx) {
        throw new Error('useOnboarding must be used within an OnboardingProvider');
    }
    return ctx;
};

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */
const buildStorageKey = (outletId: string, userId: string): string =>
    `fx_onboarding_completed_${outletId}_${userId}`;

const isOnboardingDone = (outletId: string, userId: string): boolean => {
    try {
        return localStorage.getItem(buildStorageKey(outletId, userId)) === 'true';
    } catch {
        return false;
    }
};

const markOnboardingDone = (outletId: string, userId: string): void => {
    try {
        localStorage.setItem(buildStorageKey(outletId, userId), 'true');
    } catch {
        /* silently ignore storage errors */
    }
};

const clearOnboardingDone = (outletId: string, userId: string): void => {
    try {
        localStorage.removeItem(buildStorageKey(outletId, userId));
    } catch {
        /* silently ignore */
    }
};

/* ------------------------------------------------------------------ */
/*  Provider                                                           */
/* ------------------------------------------------------------------ */
interface OnboardingProviderProps {
    children: ReactNode;
}

export const OnboardingProvider: React.FC<OnboardingProviderProps> = ({ children }) => {
    const meDetails = useSelector((state: any) => state?.items?.meDetails);
    const selectedOutlets = useSelector((state: any) => state?.items?.storeSelectedOutlet);

    const outletId = selectedOutlets?.[0]?.id || localStorage.getItem('outlet_id') || '';
    const userId = meDetails?.id || meDetails?.user_id || '';

    const [showWelcomeModal, setShowWelcomeModal] = useState(false);
    const [showCompletionModal, setShowCompletionModal] = useState(false);
    const [runTour, setRunTour] = useState(false);
    const [stepIndex, setStepIndex] = useState(0);

    // On mount / when outlet or user changes, check if onboarding is already done
    useEffect(() => {
        if (outletId && userId) {
            if (!isOnboardingDone(outletId, userId)) {
                // Small delay to let the dashboard render first
                const timer = setTimeout(() => setShowWelcomeModal(true), 1200);
                return () => clearTimeout(timer);
            }
        }
    }, [outletId, userId]);

    const startTour = useCallback(() => {
        setShowWelcomeModal(false);
        setStepIndex(0);
        // Give the modal a moment to unmount before starting the spotlight tour
        setTimeout(() => setRunTour(true), 400);
    }, []);

    const completeTour = useCallback(() => {
        setRunTour(false);
        setStepIndex(0);
        if (outletId && userId) {
            markOnboardingDone(outletId, userId);
        }
        // Show the completion celebration modal
        setTimeout(() => setShowCompletionModal(true), 300);
    }, [outletId, userId]);

    const dismissCompletion = useCallback(() => {
        setShowCompletionModal(false);
    }, []);

    const skipOnboarding = useCallback(() => {
        setShowWelcomeModal(false);
        setRunTour(false);
        setStepIndex(0);
        if (outletId && userId) {
            markOnboardingDone(outletId, userId);
        }
    }, [outletId, userId]);

    const resetTour = useCallback(() => {
        if (outletId && userId) {
            clearOnboardingDone(outletId, userId);
        }
        setStepIndex(0);
        setRunTour(false);
        setShowCompletionModal(false);
        setShowWelcomeModal(true);
    }, [outletId, userId]);

    return (
        <OnboardingContext.Provider
            value={{
                showWelcomeModal,
                showCompletionModal,
                runTour,
                setRunTour,
                stepIndex,
                setStepIndex,
                startTour,
                completeTour,
                resetTour,
                skipOnboarding,
                dismissCompletion,
            }}
        >
            {children}
        </OnboardingContext.Provider>
    );
};
